import { navigate } from 'gatsby'

import { detectLocaleFromLanguageTags } from '../config/localeDetection'

export const LOCALE_COOKIE = 'preferred_locale'
export const LOCALE_MANUAL_COOKIE = 'locale_manual'
export const LOCALE_COOKIE_MAX_AGE = 365 * 24 * 60 * 60

export type SiteLocale = 'nl_NL' | 'fr_FR' | 'en_US'

export const LOCALE_META: Record<
  SiteLocale,
  {
    htmlLang: string
    contentLanguage: string
    ogLocale: string
    hrefLang: string
    homePath: string
    bookingPath: string
  }
> = {
  nl_NL: {
    htmlLang: 'nl',
    contentLanguage: 'nl-nl',
    ogLocale: 'nl_NL',
    hrefLang: 'nl-NL',
    homePath: '/',
    bookingPath: '/reserveren',
  },
  fr_FR: {
    htmlLang: 'fr',
    contentLanguage: 'fr-fr',
    ogLocale: 'fr_FR',
    hrefLang: 'fr-FR',
    homePath: '/fr/',
    bookingPath: '/fr/reservation',
  },
  en_US: {
    htmlLang: 'en',
    contentLanguage: 'en-gb',
    ogLocale: 'en_GB',
    hrefLang: 'en-GB',
    homePath: '/en/',
    bookingPath: '/en/make-booking',
  },
}

const BOOKING_PREFIXES = ['/reserveren/', '/fr/reservation/', '/en/make-booking/']

export const isBrowser = () => typeof window !== 'undefined'

export const detectBrowserLocale = (): SiteLocale => {
  if (!isBrowser()) return 'nl_NL'

  const tags =
    navigator.languages?.length > 0
      ? [...navigator.languages]
      : [navigator.language || 'en']

  return detectLocaleFromLanguageTags(tags)
}

const readCookie = (name: string): string | null => {
  if (!isBrowser()) return null

  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))

  if (!match) return null

  return decodeURIComponent(match[1])
}

export const hasManualLocaleChoice = (): boolean =>
  readCookie(LOCALE_MANUAL_COOKIE) === '1'

export const getRootRedirectLocale = (): SiteLocale => {
  if (hasManualLocaleChoice()) {
    const preferred = getPreferredLocale()

    if (preferred) return preferred
  }

  return detectBrowserLocale()
}

export const getHomePath = (locale: string): string =>
  LOCALE_META[locale as SiteLocale]?.homePath ?? '/'

export const getBookingPath = (locale: string): string =>
  LOCALE_META[locale as SiteLocale]?.bookingPath ?? '/reserveren'

export const getBookingDetailPath = (locale: string, slug: string): string => {
  const base = getBookingPath(locale).replace(/^\//, '')
  return `/${base}/${slug}/`
}

const LOCALE_ALIASES: Record<string, SiteLocale> = {
  nl: 'nl_NL',
  nl_NL: 'nl_NL',
  'nl-NL': 'nl_NL',
  fr: 'fr_FR',
  fr_FR: 'fr_FR',
  'fr-FR': 'fr_FR',
  en: 'en_US',
  en_US: 'en_US',
  en_GB: 'en_US',
  'en-GB': 'en_US',
  'en-US': 'en_US',
}

export const resolveSiteLocale = (
  rawLocale?: string | null,
  pathname?: string
): SiteLocale => {
  if (rawLocale) {
    const alias = LOCALE_ALIASES[rawLocale]

    if (alias) return alias

    if (rawLocale in LOCALE_META) return rawLocale as SiteLocale
  }

  if (pathname) {
    const normalized = pathname.endsWith('/') ? pathname : `${pathname}/`

    if (normalized.startsWith('/en/') || normalized === '/en/') return 'en_US'
    if (normalized.startsWith('/fr/') || normalized === '/fr/') return 'fr_FR'
  }

  return 'nl_NL'
}

export const getLocaleMeta = (locale?: string | null, pathname?: string) =>
  LOCALE_META[resolveSiteLocale(locale, pathname)]

export const getAlternateOgLocales = (
  locale?: string | null,
  pathname?: string
): string[] => {
  const current = resolveSiteLocale(locale, pathname)

  return (Object.keys(LOCALE_META) as SiteLocale[])
    .filter((siteLocale) => siteLocale !== current)
    .map((siteLocale) => LOCALE_META[siteLocale].ogLocale)
}

export const getHrefLang = (locale: string): string =>
  LOCALE_META[resolveSiteLocale(locale)]?.hrefLang ?? 'nl-NL'

export const setPreferredLocale = (locale: SiteLocale): void => {
  if (!isBrowser()) return

  document.cookie = `${LOCALE_COOKIE}=${locale}; path=/; max-age=${LOCALE_COOKIE_MAX_AGE}; SameSite=Lax`
  document.cookie = `${LOCALE_MANUAL_COOKIE}=1; path=/; max-age=${LOCALE_COOKIE_MAX_AGE}; SameSite=Lax`
}

export const getPreferredLocale = (): SiteLocale | null => {
  const value = readCookie(LOCALE_COOKIE) as SiteLocale | null

  if (!value || !(value in LOCALE_META)) return null

  return value
}

export const isBookingDetailPath = (pathname: string): boolean => {
  const normalized = pathname.endsWith('/') ? pathname : `${pathname}/`

  return BOOKING_PREFIXES.some((prefix) => {
    if (!normalized.startsWith(prefix)) return false
    const rest = normalized.slice(prefix.length).replace(/\/$/, '')
    return rest.length > 0
  })
}

export const normalizeTranslationHref = (href: string, siteUrl: string): string => {
  let goTo = href

  if (goTo.indexOf('/sample-page/') !== -1) {
    goTo = `/${goTo.split('/')[1]}/`
  }

  if (goTo === '/en/sample-page/') {
    goTo = '/en/'
  }

  if (goTo === '/fr/sample-page/') {
    goTo = '/fr/'
  }

  if (goTo === `${siteUrl}/` || goTo === 'https://www.villagedeselements.com/') {
    goTo = '/'
  }

  if (goTo.startsWith('http')) return goTo

  return `${siteUrl}${goTo.startsWith('/') ? goTo : `/${goTo}`}`
}

export const getLanguage = (): SiteLocale => {
  if (isBrowser() && window.location.href.includes('/en/')) return 'en_US'
  if (isBrowser() && window.location.href.includes('/fr/')) return 'fr_FR'
  return 'nl_NL'
}

export const setLanguage = (node: { path: string }) => navigate(node.path)
